import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import moment from 'moment-timezone';
import React, {
  FunctionComponent,
  memo,
  useEffect,
  useCallback,
  useState,
} from 'react';
import { useAuthUser } from '../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { MedisanteDevice } from '../../../../pages/Device/types';
import MedSelect from '../../../../eliot-components/src/Common/MedSelect';
import { useChangeTimezoneMutation } from '../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../eliot-components/src/utils/helpers';

const availableTimeZones = moment.tz.names();

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;
  onClose?(): void;
  onCreated?(): void;
}

const ChangeTimeZoneDialog: FunctionComponent<Props> = memo(
  ({ devices, isOpen, onClose, onCreated }) => {
    const config = useConfig();
    const { authUser } = useAuthUser();
    const [changeTimeZone, { isLoading }] = useChangeTimezoneMutation();
    const [timeZone, setTimeZone] = useState(availableTimeZones[0]);
    const { enqueueSnackbar } = useSnackbar();

    const handleClose = useCallback(() => {
      if (onClose) {
        onClose();
      }
    }, [onClose]);

    const handleCreated = useCallback(() => {
      if (onCreated) {
        onCreated();
      }
    }, [onCreated]);

    useEffect(() => {
      if (isOpen && devices.length > 0) {
        setTimeZone(devices[0].timeZone || availableTimeZones[0]);
      }
    }, [devices, isOpen]);

    return (
      <>
        <Dialog
          aria-labelledby="dialog-title"
          open={isOpen}
          onClose={() => {
            handleClose();
          }}
        >
          <DialogTitle id="dialog-title">Change Time Zone</DialogTitle>
          <DialogContent>
            <MedSelect
              name="time-zone-1"
              value={timeZone}
              options={availableTimeZones}
              valueGetter={(tz: any) => tz}
              optionTextGetter={(tz: any) => tz.replace('_', ' ')}
              onChange={(e) => {
                setTimeZone(e.target.value as string);
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                handleClose();
              }}
            >
              Cancel
            </Button>
            <ButtonProgress
              variant="contained"
              color="primary"
              isLoading={isLoading}
              onClick={() => {
                changeTimeZone({
                  apiBaseURL: config.apiBaseURL,
                  orgIDs:
                    authUser.selectedOrgs.length === 1
                      ? authUser.selectedOrgs
                      : authUser.orgs.map((o) => o.id),
                  devices,
                  timeZone,
                }).then((resp: any) => {
                  if (!resp.error) {
                    handleClose();
                    handleCreated();
                    return;
                  }
                  enqueueSnackbar(parseErrorMessage(resp.error), {
                    variant: 'error',
                  });
                });
              }}
            >
              Submit
            </ButtonProgress>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default ChangeTimeZoneDialog;
